import { useEffect, useState } from "react";
import axios from "axios";
import { IoMdClose, IoMdArrowBack } from "react-icons/io";
import { useAppSelector, useAppDispatch } from "../redux/hooks";
import { useNavigate } from "react-router-dom";
import { RootState } from "../redux/store";
import { markAsRead, markAllAsRead } from "../redux/notifications/notificationSlice";
import { formatDistanceToNow, parseISO } from "date-fns";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";
import NotificationBadge from "../components/NotificationBadge";

const MetricCard = ({
  label,
  value,
  change,
  isPositive,
  icon,
}: {
  label: string;
  value: string;
  change?: string;
  isPositive?: boolean;
  icon?: React.ReactNode;
}) => (
  <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 flex flex-col gap-2 min-w-0">
    <div className="flex items-center justify-between">
      <p className="text-gray-500 text-xs sm:text-sm truncate">{label}</p>
      {icon && (
        <span className="flex items-center justify-center p-2 rounded-lg bg-[#6FD8661A]">
          {icon}
        </span>
      )}
    </div>
    <p className="font-bold text-lg sm:text-2xl truncate">{value}</p>
    {change && (
      <p className={`text-xs flex items-center gap-1 ${isPositive ? "text-[#008060]" : "text-red-500"}`}>
        {isPositive ? <FaArrowTrendUp className="w-3 h-3" /> : <FaArrowTrendDown className="w-3 h-3" />}
        {change} Since Last Week
      </p>
    )}
  </div>
);

interface DashboardStats {
  totalIncome: number;
  todayIncome: number;
  todayOrders: number;
  totalUsers: number;
}

const formatNaira = (amount: number): string => {
  return `₦${amount.toLocaleString()}`;
};

const formatCount = (n: number): string => {
  return n.toLocaleString();
};

const Notification = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("all");
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [filteredNotification, setFilteredNotification] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const { token } = useAppSelector((state: RootState) => state.auth);
  const auth = sessionStorage.getItem("authState");
  const authState = auth && JSON.parse(auth);
  const navigate = useNavigate();

  // Fetch stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/order-stats`,
          { headers: { Authorization: token } }
        );
        if (res.data?.success) {
          setDashboardStats(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };
    if (token) fetchStats();
  }, [token]);

  // Fetch activities on mount
  useEffect(() => {
    const fetchActivities = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getAllEvents`,
          { headers: { Authorization: token } }
        );
        setActivityLog(res.data);
        setFilteredNotification(res.data);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };
    if (token) fetchActivities();
  }, [token]);

  // Filter logic
  useEffect(() => {
    if (activeTab === "all") {
      setFilteredNotification(activityLog);
    } else {
      const filtered = activityLog.filter((item) => {
        const msg = item.message?.toLowerCase() || "";
        if (activeTab === "wallet") return msg.includes("wallet-update");
        if (activeTab === "referral") return msg.includes("referral");
        if (activeTab === "repayment") return msg.includes("repayment");
        return false;
      });
      setFilteredNotification(filtered);
    }
  }, [activeTab, activityLog]);

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  return (
    <main className="w-full">
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-4 transition-colors text-sm font-medium"
      >
        <IoMdArrowBack className="text-lg" /> Back to Dashboard
      </button>

      {/* Page title */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-500 text-xs sm:text-sm">
          Your latest platform alerts and updates
        </p>
      </div>

      {/* ── METRIC CARDS ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 flex flex-col gap-3 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="h-3 bg-gray-200 rounded w-24"></div>
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="h-7 bg-gray-200 rounded w-28"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          ))
        ) : (
          <>
            <MetricCard
              label="Total Income"
              value={formatNaira(dashboardStats?.totalIncome ?? 0)}
              icon={<img src="/assets/naira.svg" alt="icon" className="w-6 h-6" />}
            />
            <MetricCard
              label="Per Day Income"
              value={formatNaira(dashboardStats?.todayIncome ?? 0)}
              icon={<img src="/assets/per-day.svg" alt="icon" className="w-6 h-6" />}
            />
            <MetricCard
              label="Per Day Orders"
              value={formatCount(dashboardStats?.todayOrders ?? 0)}
              icon={<img src="/assets/per-day-order.svg" alt="icon" className="w-6 h-6" />}
            />
            <MetricCard
              label="Total Users"
              value={formatCount(dashboardStats?.totalUsers ?? 0)}
              icon={<img src="/assets/total-users.svg" alt="icon" className="w-6 h-6" />}
            />
          </>
        )}
      </div>

      {/* ── NOTIFICATIONS SECTION ── */}
      <section className="bg-white rounded-xl ps-4 sm:p-6 shadow-sm border border-gray-100 min-h-[500px]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-bold text-lg text-gray-900">Notifications</h2>
            <p className="text-xs text-gray-400">Latest platform activities</p>
          </div>
          <button
            onClick={handleMarkAllAsRead}
            className="text-[#008060] text-sm font-medium flex items-center gap-1 hover:underline"
          >
            Mark all as read <IoMdClose className="text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-gray-100 mb-6 overflow-x-auto">
          {["All", "Wallet", "Referral", "Repayment"].map((tab) => {
            const tabKey = tab.toLowerCase();
            const isActive = activeTab === tabKey;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tabKey)}
                className={`pb-2 text-sm font-medium transition-colors whitespace-nowrap ${isActive
                  ? "text-[#008060] border-b-2 border-[#008060]"
                  : "text-gray-400 hover:text-gray-600"
                  }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <span className="w-8 h-8 rounded-full border-2 border-[#008060] border-t-transparent animate-spin"></span>
            </div>
          ) : filteredNotification.length > 0 ? (
            filteredNotification
              .slice()
              .reverse()
              .map((item: any, index: number) => (
                <div key={item.id || index} className="flex items-start gap-4 pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                  <div className="w-10 h-10 rounded-full bg-[#e6f5ee] flex items-center justify-center flex-shrink-0">
                    <img src="/assets/per-day-order.svg" alt="icon" className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 capitalize">
                      <span className="font-bold">{item.message?.split(" ")[0]} {item.message?.split(" ")[1]}</span> <span className="text-gray-500">-</span> {item.message?.split(" ").slice(2).join(" ")}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {item.createdAt
                        ? formatDistanceToNow(parseISO(item.createdAt), { addSuffix: true })
                        : "Just now"}
                    </p>
                  </div>
                </div>
              ))
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <p>No notifications found</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Notification;
